import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Users } from "lucide-react";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export function PersonnelDashboardWidget() {
  const { user } = useAuth();
  const navigate = useTabNavigate();

  const { data } = useQuery({
    queryKey: ["personnel_dashboard_summary"],
    queryFn: async () => {
      // Active projects
      const { data: projects } = await supabase
        .from("personnel_projects" as any)
        .select("id, name, project_number, nte_amount")
        .eq("status", "active");
      const activeProjects = (projects ?? []) as any[];

      // Workers
      const { count: workerCount } = await supabase
        .from("tm_workers" as any)
        .select("id", { count: "exact", head: true })
        .eq("is_active", true);

      // Timesheets for billing
      const { data: timesheets } = await supabase
        .from("tm_timesheets" as any)
        .select("total_billed, margin_pct, personnel_project_id, week_ending");
      const sheets = (timesheets ?? []) as any[];

      // This week & MTD
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0];
      const thisWeekSheets = sheets; // simplified — all recent
      const mtdBilled = sheets
        .filter((s: any) => s.week_ending >= startOfMonth)
        .reduce((sum: number, s: any) => sum + (s.total_billed || 0), 0);
      const totalBilled = sheets.reduce((sum: number, s: any) => sum + (s.total_billed || 0), 0);
      const avgMargin = sheets.length > 0
        ? sheets.reduce((sum: number, s: any) => sum + (s.margin_pct || 0), 0) / sheets.length
        : 0;

      // NTE warnings
      const nteWarnings = activeProjects
        .filter((p: any) => p.nte_amount)
        .map((p: any) => {
          const projBilled = sheets
            .filter((s: any) => s.personnel_project_id === p.id)
            .reduce((sum: number, s: any) => sum + (s.total_billed || 0), 0);
          const pct = (projBilled / p.nte_amount) * 100;
          return { ...p, pct };
        })
        .filter((p: any) => p.pct >= 80);

      return {
        activeCount: activeProjects.length,
        workerCount: workerCount ?? 0,
        mtdBilled,
        avgMargin,
        nteWarnings,
      };
    },
    enabled: !!user,
  });

  if (!data || data.activeCount === 0) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Users className="h-4 w-4" /> Personnel Projects
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Active Projects</span>
          <span className="font-semibold">{data.activeCount}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Workers on Payroll</span>
          <span className="font-semibold">{data.workerCount}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">MTD Billed</span>
          <span className="font-semibold">{fmt(data.mtdBilled)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Avg Margin</span>
          <span className={`font-semibold ${data.avgMargin >= 25 ? "text-emerald-600" : data.avgMargin >= 15 ? "text-amber-600" : "text-destructive"}`}>
            {data.avgMargin.toFixed(1)}%
          </span>
        </div>
        {data.nteWarnings.map((w: any) => (
          <p key={w.id} className="text-xs text-amber-600 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> {w.project_number} at {w.pct.toFixed(0)}% NTE
          </p>
        ))}
        <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => navigate("/controller/projects")}>
          View All Personnel →
        </Button>
      </CardContent>
    </Card>
  );
}
